import SliderSlick from 'react-slick'
import { useRef, useState, useEffect } from 'react'
import './SliderMobileGap.css'

interface Slide {
  type: 'image' | 'video' | 'youtube';
  url: string;
  alt?: string;
}

// Variables responsive manejadas por CSS
// Mobile/Tablet: 237x312
// Desktop (1024px+): 335.66x447.221

const Slider = ({ data }: { data: Slide[] }) => {
  const sliderRef = useRef<any>(null)
  const [, setCurrentSlide] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const [currentModalSlide, setCurrentModalSlide] = useState(0)

  const settings = {
    dots: false, // Disable default dots since we're using custom ones
    infinite: false,
    speed: 500,
    slidesToShow: 4, // Exactamente 4 slides en desktop
    slidesToScroll: 1,
    arrows: false, // Disable default arrows since we're using custom ones
    centerMode: false,
    focusOnSelect: true,
    variableWidth: false,
    swipeToSlide: false, // Evitar que se muestren fracciones al hacer swipe
    beforeChange: (current: number, next: number) => setCurrentSlide(next),
    responsive: [
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1.5,
          centerMode: false,
          centerPadding: '0px',
          arrows: false,
          focusOnSelect: true,
          variableWidth: false,
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 2,
          centerMode: false,
          centerPadding: '0px',
          arrows: false,
          variableWidth: false,
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3, // 3 slides para desktop pequeño
          centerMode: false,
          centerPadding: '0px',
          arrows: false,
          variableWidth: false,
          swipeToSlide: false,
        }
      }
    ]
  };

  const getYouTubeEmbedUrl = (url: string): string => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = match && match[2].length === 11 ? match[2] : null;
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
    }
    return url;
  };

  const handlePrevSlide = () => {
    sliderRef.current?.slickPrev()
  }

  const handleNextSlide = () => {
    sliderRef.current?.slickNext()
  }

  // Modal functions
  const openModal = (slideIndex: number) => {
    setCurrentModalSlide(slideIndex)
    setModalOpen(true)
    document.body.style.overflow = 'hidden' // Prevent background scroll
  }

  const closeModal = () => {
    setModalOpen(false)
    document.body.style.overflow = 'unset' // Restore scroll
  }

  const nextModalSlide = () => {
    setCurrentModalSlide((prev) => (prev + 1) % data.length)
  }

  const prevModalSlide = () => {
    setCurrentModalSlide((prev) => (prev - 1 + data.length) % data.length)
  }

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!modalOpen) return
      
      if (e.key === 'Escape') {
        closeModal()
      } else if (e.key === 'ArrowRight') {
        nextModalSlide()
      } else if (e.key === 'ArrowLeft') {
        prevModalSlide()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [modalOpen, data.length])

  // Calculate total number of dots based on responsive settings
  // const getTotalDots = () => {
  //   if (window.innerWidth <= 600) {
  //     return Math.ceil(data.length / 1.5) // 1.5 slides shown
  //   } else if (window.innerWidth <= 900) {
  //     return Math.ceil(data.length / 2) // 2 slides shown
  //   } else if (window.innerWidth <= 1024) {
  //     return Math.ceil(data.length / 3) // 3 slides shown
  //   } else {
  //     return Math.ceil(data.length / 4) // 4 slides shown
  //   }
  // }

  // const totalDots = Math.max(1, getTotalDots())

  return (
    <>
      <div className="slider-container">
        <SliderSlick {...settings} className="slider-mobile-gap" ref={sliderRef}>
          {data.map((slide, idx) => (
            <div key={idx}>
              <div className="slide-content">
                <div
                  className="slide-media-container"
                  style={{
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                  onClick={() => openModal(idx)}
                >
                  {slide.type === 'image' && (
                    <>
                      <img
                        src={slide.url}
                        alt={slide.alt || ''}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          display: 'block'
                        }}
                        loading="lazy"
                      />
                      <div className="slide-overlay">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15 3H21V9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 21H3V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M21 3L14 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3 21L10 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </>
                  )}
                  {slide.type === 'video' && (
                    <>
                      <video
                        src={slide.url}
                        preload="metadata"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                      <div className="slide-overlay">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5V19L19 12L8 5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </>
                  )}
                  {slide.type === 'youtube' && (
                    <>
                      <iframe
                        width="100%"
                        height="100%"
                        src={slide.url.replace('autoplay=1', 'autoplay=0')}
                        title={slide.alt || 'YouTube video'}
                        frameBorder="0"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          display: 'block',
                          pointerEvents: 'none'
                        }}
                      />
                      <div className="slide-overlay">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.54 6.42C22.44 6.04 22.24 5.7 21.96 5.42C21.68 5.14 21.34 4.94 20.96 4.84C19.13 4.5 12 4.5 12 4.5C12 4.5 4.87 4.5 3.04 4.84C2.66 4.94 2.32 5.14 2.04 5.42C1.76 5.7 1.56 6.04 1.46 6.42C1.12 8.25 1.12 12 1.12 12C1.12 12 1.12 15.75 1.46 17.58C1.56 17.96 1.76 18.3 2.04 18.58C2.32 18.86 2.66 19.06 3.04 19.16C4.87 19.5 12 19.5 12 19.5C12 19.5 19.13 19.5 20.96 19.16C21.34 19.06 21.68 18.86 21.96 18.58C22.24 18.3 22.44 17.96 22.54 17.58C22.88 15.75 22.88 12 22.88 12C22.88 12 22.88 8.25 22.54 6.42V6.42Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9.75 15.02L15.5 12L9.75 8.98V15.02Z" fill="white"/>
                        </svg>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </SliderSlick>
        
        {/* Custom Dots Navigation */}
        <div className="dots">
          <div className="dots__container">
            <button 
              // className={currentSlide === 0 ? 'invisible' : ''} 
              onClick={handlePrevSlide}
            >
              <img 
                src={`${AZURE_BASE_URL}/arrow-onboarding-step.webp`} 
                alt="Previous" 
                className="arrow-left" 
              />
            </button>

            {/* <div className="dots-indicator !hidden">
              {Array.from({ length: totalDots }, (_, index) => (
                <button
                  key={index}
                  className={`dot ${currentSlide === index ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div> */}

            <button 
              // className={currentSlide === totalDots - 1 ? 'invisible' : ''} 
              onClick={handleNextSlide}
            >
              <img 
                src={`${AZURE_BASE_URL}/arrow-onboarding-step.webp`} 
                alt="Next" 
                className="arrow-right" 
              />
            </button>
          </div>
        </div>
      </div>

      {/* Modal Gallery */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="modal-close" onClick={closeModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Navigation arrows */}
            {data.length > 1 && (
              <>
                <button className="modal-nav modal-prev" onClick={prevModalSlide}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button className="modal-nav modal-next" onClick={nextModalSlide}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </>
            )}

            {/* Media content */}
            <div className="modal-media">
              {data[currentModalSlide].type === 'image' && (
                <img
                  src={data[currentModalSlide].url}
                  alt={data[currentModalSlide].alt || ''}
                  className="modal-image"
                />
              )}
              {data[currentModalSlide].type === 'video' && (
                <video
                  src={data[currentModalSlide].url}
                  controls
                  autoPlay
                  className="modal-video"
                />
              )}
              {data[currentModalSlide].type === 'youtube' && (
                <iframe
                  src={getYouTubeEmbedUrl(data[currentModalSlide].url)}
                  title={data[currentModalSlide].alt || 'YouTube video'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="modal-iframe"
                />
              )}
            </div>

            {/* Counter */}
            {data.length > 1 && (
              <div className="modal-counter">
                {currentModalSlide + 1} / {data.length}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default Slider